import React from 'react';
import { COLORS, SIZES, RADIUS, FONT_URL } from './design-tokens';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const dateOnly = dateStr.split('T')[0];
  if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateOnly.split('-');
    return `${day}/${month}/${year}`;
  }
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const getPaymentMethodLabel = (method) => {
  const labels = {
    'cash': 'Dinheiro',
    'pix': 'PIX',
    'credit_card': 'Cartão de Crédito',
    'debit_card': 'Cartão de Débito',
    'bank_transfer': 'Transferência',
    'cheque': 'Cheque',
    'boleto': 'Boleto',
    'cash_installments': 'Din. Parcelado',
    'pix_direct': 'PIX Fornecedor',
    'cheque_direct': 'Cheque Fornecedor',
  };
  return labels[method] || method || '-';
};

const getStatusLabel = (status) => {
  const labels = {
    'PENDING': 'Pendente',
    'PARTIAL': 'Parcial',
    'RECEIVED': 'Recebido',
    'COMPLETED': 'Concluído',
  };
  return labels[status] || status || '-';
};

const StatusBadge = ({ status }) => {
  const colors = {
    'PENDING': { bg: COLORS.pendingYellowBg, text: COLORS.pendingYellowDark },
    'PARTIAL': { bg: '#dbeafe', text: '#1e40af' },
    'RECEIVED': { bg: COLORS.receivedGreenBg, text: COLORS.subtotalGreenDark },
    'COMPLETED': { bg: COLORS.receivedGreenBg, text: COLORS.subtotalGreenDark },
  };
  const color = colors[status] || { bg: '#e5e7eb', text: '#374151' };

  return (
    <span
      style={{
        padding: `${SIZES.badgePaddingV} ${SIZES.badgePaddingH}`,
        borderRadius: RADIUS.badge,
        fontSize: '9px',
        fontWeight: 600,
        background: color.bg,
        color: color.text,
        display: 'inline-block'
      }}
    >
      {getStatusLabel(status)}
    </span>
  );
};

const SummaryBox = ({ balance, totals, extraIncomes, projectAbates, scheduledCheques }) => {
  const extraTotal = extraIncomes ? extraIncomes.reduce((sum, e) => sum + (parseFloat(e.total_amount) || 0), 0) : 0;
  const grandTotal = (parseFloat(balance.total_amount) || 0) + extraTotal;
  const abatesTotal = projectAbates ? projectAbates.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) : 0;
  const totalReceived = (parseFloat(totals.received) || 0) + abatesTotal;
  const pendingExtra = extraIncomes ? extraIncomes.reduce((sum, e) => {
    const paidInstallments = e.installments_paid || 0;
    const pendingInstallments = (e.installments || 1) - paidInstallments;
    const installmentAmount = e.total_amount / (e.installments || 1);
    return sum + (pendingInstallments * installmentAmount);
  }, 0) : 0;
  const totalRemaining = (parseFloat(totals.expected) || 0) + pendingExtra;

  const hasScheduledCheques = scheduledCheques && scheduledCheques.length > 0;
  const totalCheques = hasScheduledCheques ? scheduledCheques.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) : 0;
  const inHandCount = hasScheduledCheques ? scheduledCheques.filter(e => e.cheque_in_hand).length : 0;

  return (
    <div
      className="receipt-summary-box"
      style={{
        background: COLORS.backgroundSecondary,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        padding: '5mm',
        marginBottom: '5mm'
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: hasScheduledCheques ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)',
          gap: '3mm',
          textAlign: 'center'
        }}
      >
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.textSecondary, letterSpacing: '0.5px', marginBottom: '1mm' }}>
            VALOR TOTAL
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.textPrimary }}>
            {formatCurrency(grandTotal)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.textSecondary, letterSpacing: '0.5px', marginBottom: '1mm' }}>
            ENTRADA
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.primary }}>
            {balance.entry_amount > 0 ? formatCurrency(balance.entry_amount) : '-'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.textSecondary, letterSpacing: '0.5px', marginBottom: '1mm' }}>
            PARCELAS
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.textPrimary }}>
            {balance.installments}x
          </div>
        </div>
        {hasScheduledCheques && (
          <div>
            <div style={{ fontSize: '9px', fontWeight: 600, color: '#7c3aed', letterSpacing: '0.5px', marginBottom: '1mm' }}>
              CHEQUES AGENDADOS
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#6b21a8' }}>
              {formatCurrency(totalCheques)}
            </div>
            <div style={{ fontSize: '8px', color: '#7c3aed', fontWeight: 600 }}>
              {scheduledCheques.length}x • {inHandCount} em mãos
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.textSecondary, letterSpacing: '0.5px', marginBottom: '1mm' }}>
            RECEBIDO
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.receivedGreen }}>
            {formatCurrency(totalReceived)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.textSecondary, letterSpacing: '0.5px', marginBottom: '1mm' }}>
            RESTANTE
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.pendingYellowDark }}>
            {formatCurrency(totalRemaining)}
          </div>
        </div>
      </div>
    </div>
  );
};

const TableHeader = ({ title, color }) => (
  <div
    style={{
      background: color,
      color: COLORS.card,
      padding: SIZES.tableHeaderPadding,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center'
    }}
  >
    {title}
  </div>
);

const ReceivedTable = ({ received }) => {
  if (!received || received.length === 0) return null;

  const subtotal = received.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="ENTRADAS RECEBIDAS" color={COLORS.primary} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
        <thead>
          <tr style={{ background: COLORS.subtotalBg }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '22mm' }}>DATA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary }}>DESCRIÇÃO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '28mm' }}>FORMA PAGTO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: COLORS.textSecondary, width: '28mm' }}>VALOR</th>
          </tr>
        </thead>
        <tbody>
          {received.map((entry, index) => (
            <tr key={entry.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#fafcfb' }}>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {formatDate(entry.date)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {entry.description || '-'}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                {getPaymentMethodLabel(entry.payment_method)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 600, color: COLORS.receivedGreen, paddingRight: '4mm' }}>
                {formatCurrency(entry.amount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: COLORS.subtotalBg }}>
            <td colSpan={3} style={{ padding: '3mm', fontWeight: 700, color: COLORS.subtotalGreenDark, fontSize: '11px' }}>
              SUBTOTAL RECEBIDO
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: COLORS.subtotalGreenDark, fontSize: '11px', paddingRight: '4mm' }}>
              {formatCurrency(subtotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ScheduledChequesTable = ({ scheduledCheques }) => {
  if (!scheduledCheques || scheduledCheques.length === 0) return null;

  const totalCheques = scheduledCheques.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const inHandCount = scheduledCheques.filter(e => e.cheque_in_hand).length;

  return (
    <div
      className="receipt-section-card"
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="CHEQUES AGENDADOS / PRÉ-DATADOS EM CARTEIRA" color="#7c3aed" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
        <thead>
          <tr style={{ background: '#f5f3ff' }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: '#5b21b6', width: '22mm' }}>DATA (BOM PARA)</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>DESCRIÇÃO / PARCELA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: '#5b21b6', width: '22mm' }}>FORMA PAGTO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'center', fontWeight: 600, color: '#5b21b6', width: '38mm' }}>POSSE DO CHEQUE</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: '#5b21b6', width: '26mm', paddingRight: '4mm' }}>VALOR</th>
          </tr>
        </thead>
        <tbody>
          {scheduledCheques.map((entry, index) => {
            const inHand = entry.cheque_in_hand === true;
            return (
              <tr key={entry.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#faf5ff' }}>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary, fontWeight: 500 }}>
                  {formatDate(entry.date)}
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                  {entry.description || '-'}
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                  Cheque
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'center' }}>
                  {inHand ? (
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1.5px 6px',
                          borderRadius: '10px',
                          fontSize: '8.5px',
                          fontWeight: 700,
                          background: '#dcfce7',
                          color: '#15803d',
                          border: '1px solid #86efac'
                        }}
                      >
                        ✓ EM MÃOS (RECEBIDO)
                      </span>
                      {entry.cheque_received_at && (
                        <div style={{ fontSize: '7.5px', color: '#15803d', marginTop: '0.8mm', fontWeight: 500 }}>
                          Recebido em: {formatDate(entry.cheque_received_at)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1.5px 6px',
                        borderRadius: '10px',
                        fontSize: '8.5px',
                        fontWeight: 600,
                        background: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #fde68a'
                      }}
                    >
                      AGUARDANDO ENTREGA
                    </span>
                  )}
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 700, color: '#7c3aed', paddingRight: '4mm' }}>
                  {formatCurrency(entry.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f5f3ff' }}>
            <td colSpan={4} style={{ padding: '3mm', fontWeight: 700, color: '#5b21b6', fontSize: '11px' }}>
              TOTAL EM CHEQUES AGENDADOS ({scheduledCheques.length}x • {inHandCount} em mãos)
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontSize: '11px', paddingRight: '4mm' }}>
              {formatCurrency(totalCheques)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const PendingTable = ({ expected }) => {
  if (!expected || expected.length === 0) return null;

  const totalPredicted = expected.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  return (
    <div
      className="receipt-section-card"
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="ENTRADAS PREVISTAS" color={COLORS.pendingYellow} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
        <thead>
          <tr style={{ background: COLORS.pendingYellowBg }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '20mm' }}>DATA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary }}>DESCRIÇÃO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '25mm' }}>FORMA PAGTO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '22mm' }}>STATUS</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: COLORS.textSecondary, width: '26mm' }}>VALOR</th>
          </tr>
        </thead>
        <tbody>
          {expected.map((entry, index) => (
            <tr key={entry.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#fefcf5' }}>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {formatDate(entry.date)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {entry.description || '-'}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                {getPaymentMethodLabel(entry.payment_method)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}` }}>
                <StatusBadge status={entry.status} />
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 600, color: COLORS.pendingYellowDark, paddingRight: '4mm' }}>
                {entry.has_discount ? (
                  <div>
                    <span style={{ textDecoration: 'line-through', color: COLORS.textSecondary, fontSize: '9px' }}>
                      {formatCurrency(entry.original_amount)}
                    </span>
                    <br />
                    <span>{formatCurrency(entry.amount)}</span>
                  </div>
                ) : (
                  formatCurrency(entry.amount)
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: COLORS.pendingYellowBg }}>
            <td colSpan={4} style={{ padding: '3mm', fontWeight: 700, color: COLORS.pendingYellowDark, fontSize: '11px' }}>
              TOTAL PREVISTO
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: COLORS.pendingYellowDark, fontSize: '11px', paddingRight: '4mm' }}>
              {formatCurrency(totalPredicted)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const AbatesTable = ({ projectAbates }) => {
  if (!projectAbates || projectAbates.length === 0) return null;

  const totalAbated = projectAbates.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  return (
    <div
      className="receipt-section-card"
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="ABATES DE DESPESAS" color="#7c3aed" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
        <thead>
          <tr style={{ background: '#f5f3ff' }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '20mm' }}>DATA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary }}>DESPESA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '25mm' }}>FORNECEDOR</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '18mm' }}>STATUS</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: COLORS.textSecondary, width: '24mm' }}>VALOR</th>
          </tr>
        </thead>
        <tbody>
          {projectAbates.map((abate, index) => (
            <tr key={abate.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#fafaf9' }}>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {abate.payment_date ? formatDate(abate.payment_date) : formatDate(abate.created_at)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                <div>{abate.cost_description || '-'}</div>
                {abate.entries && abate.entries.length > 0 && (
                  <div style={{ fontSize: '8px', color: COLORS.textSecondary, marginTop: '0.5mm' }}>
                    {abate.entries.map((entry, i) => (
                      <div key={entry.id || i}>• {entry.income_description || `Entrada ${entry.expected_income_id}`}: {formatCurrency(entry.amount_abated)}</div>
                    ))}
                  </div>
                )}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                {abate.supplier_name || '-'}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}` }}>
                <StatusBadge status={abate.status === 'PAID' ? 'RECEIVED' : abate.status} />
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 600, color: '#7c3aed', paddingRight: '4mm' }}>
                {formatCurrency(abate.amount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f5f3ff' }}>
            <td colSpan={4} style={{ padding: '3mm', fontWeight: 700, color: '#5b21b6', fontSize: '11px' }}>
              TOTAL ABATIDO
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: '#5b21b6', fontSize: '11px', paddingRight: '4mm' }}>
              {formatCurrency(totalAbated)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ExtraIncomesTable = ({ extraIncomes }) => {
  if (!extraIncomes || extraIncomes.length === 0) return null;

  const totalAmount = extraIncomes.reduce((sum, e) => sum + (parseFloat(e.total_amount) || 0), 0);
  const totalReceived = extraIncomes.reduce((sum, e) => {
    const paidInstallments = e.installments_paid || 0;
    const installmentAmount = e.total_amount / e.installments;
    return sum + (paidInstallments * installmentAmount);
  }, 0);

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="RECEBIMENTOS EXTRAS" color={COLORS.extraOrange} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
        <thead>
          <tr style={{ background: COLORS.extraOrangeBg }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '30mm' }}>DESCRIÇÃO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '18mm' }}>PARCELAS</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '22mm' }}>STATUS</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '22mm' }}>RECEBIDO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: COLORS.textSecondary, width: '25mm' }}>VALOR TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {extraIncomes.map((extra, index) => {
            const receivedAmount = (extra.installments_paid || 0) * (extra.total_amount / extra.installments);
            return (
              <tr key={extra.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#fcfcfb' }}>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                  <div style={{ fontWeight: 500 }}>{extra.description || '-'}</div>
                  {extra.entries && extra.entries.length > 0 && (
                    <div style={{ fontSize: '8px', color: COLORS.textSecondary, marginTop: '0.5mm' }}>
                      {extra.entries.slice(0, 2).map((entry, i) => (
                        <div key={entry.id || i}>• {formatDate(entry.date)}: {formatCurrency(entry.amount || entry.value)}</div>
                      ))}
                      {extra.entries.length > 2 && <div>... +{extra.entries.length - 2} mais</div>}
                    </div>
                  )}
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                  {extra.installments}x
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}` }}>
                  <StatusBadge status={extra.status === 'RECEIVED' ? 'RECEIVED' : extra.status} />
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.receivedGreen, fontWeight: 600 }}>
                  {formatCurrency(receivedAmount)}
                </td>
                <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 600, color: COLORS.extraOrangeDark, paddingRight: '4mm' }}>
                  {formatCurrency(extra.total_amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: COLORS.extraOrangeBg }}>
            <td colSpan={3} style={{ padding: '3mm', fontWeight: 700, color: COLORS.extraOrangeDark, fontSize: '11px' }}>
              TOTAL EXTRAS
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: COLORS.subtotalGreenDark, fontSize: '11px' }}>
              {formatCurrency(totalReceived)}
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: COLORS.extraOrangeDark, fontSize: '11px', paddingRight: '4mm' }}>
              {formatCurrency(totalAmount)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const DiscountsTable = ({ discounts, totals }) => {
  if (!discounts || discounts.length === 0) return null;

  const totalDiscounts = totals?.total_discounts || discounts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: RADIUS.card,
        overflow: 'visible',
        marginBottom: '5mm'
      }}
    >
      <TableHeader title="DESCONTOS CONCEDIDOS" color="#7c3aed" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
        <thead>
          <tr style={{ background: '#f5f3ff' }}>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '25mm' }}>DATA</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary }}>DESCRIÇÃO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'left', fontWeight: 600, color: COLORS.textSecondary, width: '20mm' }}>MOTIVO</th>
            <th style={{ padding: SIZES.tableCellPadding, textAlign: 'right', fontWeight: 600, color: COLORS.textSecondary, width: '25mm' }}>VALOR</th>
          </tr>
        </thead>
        <tbody>
          {discounts.map((discount, index) => (
            <tr key={discount.id || index} style={{ background: index % 2 === 0 ? COLORS.card : '#fafaf9' }}>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {formatDate(discount.date)}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textPrimary }}>
                {discount.parcel_description || '-'}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, color: COLORS.textSecondary }}>
                {discount.reason || '-'}
              </td>
              <td style={{ padding: SIZES.tableCellPadding, borderTop: `1px solid ${COLORS.line}`, textAlign: 'right', fontWeight: 600, color: '#7c3aed', paddingRight: '4mm' }}>
                - {formatCurrency(discount.amount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f5f3ff' }}>
            <td colSpan={3} style={{ padding: '3mm', fontWeight: 700, color: '#5b21b6', fontSize: '11px' }}>
              TOTAL DE DESCONTOS
            </td>
            <td style={{ padding: '3mm', textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontSize: '11px', paddingRight: '4mm' }}>
              - {formatCurrency(totalDiscounts)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const InitialBalanceReceiptTemplate = ({ data }) => {
  const { balance, received, expected, scheduled_cheques, regular_expected, totals, company, project, projectAbates, extraIncomes, discounts } = data;

  const scheduledChequesList = Array.isArray(scheduled_cheques)
    ? scheduled_cheques
    : [];

  const regularExpectedList = Array.isArray(regular_expected)
    ? regular_expected
    : (expected || []);

  return (
    <>
      <link rel="stylesheet" href={FONT_URL} />
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 10mm 15mm 10mm;
          }
          body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .receipt-print-wrapper {
            width: 100% !important;
            min-height: auto !important;
            padding: 0 !important;
            background: #fff !important;
            box-shadow: none !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          thead {
            display: table-header-group !important;
          }
          tfoot {
            display: table-footer-group !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .receipt-section-card {
            page-break-inside: auto !important;
            break-inside: auto !important;
            margin-bottom: 4mm !important;
          }
          .receipt-header-grid,
          .receipt-summary-box {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
      <div
        className="receipt-print-wrapper"
        style={{
          width: SIZES.pageWidth,
          minHeight: '297mm',
          padding: SIZES.margin,
          background: COLORS.backgroundMain,
          fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
          boxSizing: 'border-box',
          color: COLORS.textPrimary,
          position: 'relative',
          wordBreak: 'normal',
          overflowWrap: 'break-word',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          className="receipt-header-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: `${SIZES.leftColumnWidth} ${SIZES.rightColumnWidth}`,
            columnGap: SIZES.columnGap,
            marginBottom: '5mm',
            flexShrink: 0
          }}
        >
          <div
            style={{
              background: COLORS.primary,
              borderRadius: `${RADIUS.card} 0 0 ${RADIUS.card}`,
              position: 'relative',
              overflow: 'hidden',
              padding: '6mm',
              display: 'flex',
              alignItems: 'flex-end',
              height: SIZES.headerHeight
            }}
          >
            <div style={{ position: 'relative', zIndex: 1, color: COLORS.card }}>
              {company && company.logo ? (
                <img
                  src={company.logo}
                  alt="Logo"
                  style={{
                    height: '22mm',
                    maxWidth: '65mm',
                    objectFit: 'contain',
                    marginBottom: '1mm'
                  }}
                />
              ) : null}
              <div style={{ fontSize: '8px', fontWeight: 600, opacity: 0.9, letterSpacing: '1.5px', marginTop: '2mm' }}>
                SANTIAGO INHAPIM PLANEJADOS
              </div>
            </div>
          </div>

          <div
            style={{
              background: COLORS.card,
              borderRadius: `0 ${RADIUS.card} ${RADIUS.card} 0`,
              padding: '5mm',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              border: `1px solid ${COLORS.line}`,
              borderLeft: 'none',
              height: SIZES.headerHeight,
              boxSizing: 'border-box'
            }}
          >
            <div style={{ marginBottom: 'auto' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: COLORS.primary, letterSpacing: '0.5px' }}>
                RECIBO FINANCEIRO
              </div>
              <div style={{ fontSize: '8px', color: COLORS.textSecondary, marginTop: '0.5mm' }}>
                Demonstrativo de Recibos
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '22mm 1fr',
                rowGap: '1mm',
                columnGap: '2mm',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '8px', fontWeight: 600, color: COLORS.textSecondary }}>CLIENTE:</span>
              <span style={{ fontSize: '9px', color: COLORS.textPrimary, fontWeight: 500 }}>
                {data?.clientName || '-'}
              </span>
              <span style={{ fontSize: '8px', fontWeight: 600, color: COLORS.textSecondary }}>PROJETO:</span>
              <span style={{ fontSize: '9px', color: COLORS.textPrimary, fontWeight: 500 }}>
                {project && project.number ? project.number : balance.id || '-'}
              </span>
              <span style={{ fontSize: '8px', fontWeight: 600, color: COLORS.textSecondary }}>GERADO EM:</span>
              <span style={{ fontSize: '9px', color: COLORS.textPrimary }}>
                {project && project.generatedAt ? formatDate(project.generatedAt) : '-'}
              </span>
            </div>
          </div>
        </div>

        {balance.description && (
          <div
            style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.line}`,
              borderRadius: '6px',
              padding: '3mm 4mm',
              marginBottom: '4mm',
              fontSize: '10px',
              color: COLORS.textSecondary
            }}
          >
            <strong style={{ color: COLORS.textPrimary }}>Descrição:</strong> {balance.description}
          </div>
        )}

        <SummaryBox balance={balance} totals={totals} extraIncomes={extraIncomes} projectAbates={projectAbates} scheduledCheques={scheduledChequesList} />

        <ReceivedTable received={received} />
        <DiscountsTable discounts={discounts} totals={totals} />
        <ScheduledChequesTable scheduledCheques={scheduledChequesList} />
        <PendingTable expected={regularExpectedList} />
        <AbatesTable projectAbates={projectAbates} />
        <ExtraIncomesTable extraIncomes={extraIncomes} />

        <div
          style={{
            marginTop: 'auto',
            paddingTop: '4mm',
            borderTop: `1px solid ${COLORS.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}
        >
          <div style={{ fontSize: '8px', color: COLORS.textSecondary }}>
            <div>Gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</div>
            {company && (company.address || company.phone) && (
              <div style={{ marginTop: '1mm' }}>
                {company.address && <span>{company.address}</span>}
                {company.phone && <span> | {company.phone}</span>}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '9px', fontWeight: 600, color: COLORS.primary, letterSpacing: '0.5px' }}>
              {company && company.name ? company.name.toUpperCase() : 'EMPRESA'}
            </div>
            <div style={{ fontSize: '8px', color: COLORS.textSecondary }}>
              {company && company.document ? `CNPJ: ${company.document}` : ''}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InitialBalanceReceiptTemplate;
